import { router, useFocusEffect, useLocalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetPaymentMethodList } from '@/api/hooks/paymentAccountsHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import TokenizedCardView from '@/components/paymentMethods/TokenizedCardView';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { decryptObject, encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';

export interface TokenizedCreditCardData {
  card_key?: string;
  card_number_last_four_digits: string;
  card_type: string;
  expiration_date: string;
  name_on_card: string;
}

const CreditCardList = () => {
  const navigation = useNavigation();

  const { payload } = useLocalSearchParams<{ payload: string }>();

  const { data, refetch, isPending } = useGetPaymentMethodList('');

  const [payloadState, setPayloadState] = useState<{
    donationData: string;
    individualNuveiUser: string;
    fromIOS?: string;
    influencerCode?: string;
    routeWithSelectedMethod?: string;
    subscriptionItem?: string;
    showId?: string;
  }>();

  useFocusEffect(
    useCallback(() => {
      refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        donationData: string;
        individualNuveiUser: string;
        fromIOS?: string;
        influencerCode?: string;
        routeWithSelectedMethod?: string;
        subscriptionItem?: string;
        showId?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useLayoutEffect(() => {
    if (payloadState?.donationData)
      navigation.setOptions({
        headerTitle: () => <Title label={i18n.t('donation.select_payment_method')} />
      });
  }, [payloadState?.donationData, navigation]);

  const creditCards = data?.paymentsafe_cards.flatMap((card) => card);

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        {isPending ? (
          <View style={styles.loaderContainer}>
            <DotsLoader />
          </View>
        ) : (
          <FlatList
            data={creditCards}
            showsVerticalScrollIndicator={false}
            style={styles.list}
            ItemSeparatorComponent={() => <Divider style={styles.separator} />}
            ListEmptyComponent={<Text style={styles.emptyList}>{i18n.t('credit_cards.saved_empty')}</Text>}
            keyExtractor={(_, index) => index.toString()}
            renderItem={({ item }) => (
              <TokenizedCardView
                tokenizedCard={item}
                onDelete={refetch}
                {...((payloadState?.donationData || payloadState?.routeWithSelectedMethod) && {
                  onPress: async () => {
                    const newPayload = { ...payloadState, tokenizedCard: JSON.stringify(item) };

                    const encrypted = await encryptObject(newPayload);

                    router.navigate({
                      pathname: '/(app)/paymentMethods/billingAddress',
                      params: {
                        payload: encodeURIComponent(encrypted)
                      }
                    });
                  }
                })}
              />
            )}
            ListFooterComponentStyle={styles.footer}
            ListFooterComponent={() => {
              if (payloadState?.donationData || payloadState?.routeWithSelectedMethod)
                return (
                  <BrandedButton
                    style={styles.addCCButton}
                    onPress={async () => {
                      const newPayload = { ...payloadState };

                      const encrypted = await encryptObject(newPayload);

                      router.navigate({
                        pathname: '/(app)/paymentMethods/addCreditCard',
                        params: {
                          payload: encodeURIComponent(encrypted)
                        }
                      });
                    }}>
                    {i18n.t('credit_cards.add')}
                  </BrandedButton>
                );
            }}
          />
        )}
      </View>
    </SafeAreaView>
  );
};

export default CreditCardList;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  list: {
    flex: 1
  },

  addCCButton: {
    width: '100%'
  },

  container: {
    flex: 1,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  emptyList: {
    alignSelf: 'center',
    marginVertical: 25,
    fontSize: 14
  },

  separator: {
    marginVertical: 10
  },

  footer: {
    flex: 1,
    paddingVertical: 20
  }
});
